import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { settingsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Server, Database, HardDrive, Users, Activity, Wallet,
  IndianRupee, Clock, RefreshCw, Zap, Image, Layers,
  ChevronRight, Home, CheckCircle, XCircle, AlertCircle,
  Cpu, Globe, ArrowUpRight, TrendingUp, BarChart3,
} from 'lucide-react';

// ===== CIRCULAR GAUGE =====
const CircularGauge = ({ value, label, size = 90, strokeWidth = 7, color = '#4338ca', maxVal = 100 }) => {
  const pct = Math.min((value / maxVal) * 100, 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (pct / 100) * circumference;
  const colorLevel = pct > 80 ? '#ef4444' : pct > 60 ? '#f59e0b' : color;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={colorLevel} strokeWidth={strokeWidth}
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease, stroke 0.5s ease' }} />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-lg font-bold text-slate-900 font-mono">{typeof value === 'number' ? value.toFixed(1) : value}</span>
        <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
};

// ===== PROGRESS BAR =====
const ProgressBar = ({ value, max, color = '#4338ca' }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
};

// ===== STAT CARD =====
const StatCard = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="si-stat-card" data-testid={testId}>
    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}12` }}>
      <Icon className="w-[18px] h-[18px]" style={{ color: accent }} strokeWidth={1.8} />
    </div>
    <div className="mt-2.5">
      <p className="text-xl font-bold text-slate-900 font-mono tracking-tight">{value}</p>
      <p className="text-[10px] text-slate-400 mt-0.5 font-medium">{sub || label}</p>
    </div>
    <p className="absolute top-3 right-3 text-[9px] font-bold text-slate-300 uppercase tracking-wider">{label}</p>
  </div>
);

export default function ServerInfo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [statsHistory, setStatsHistory] = useState([]);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await settingsAPI.getServerInfo();
      setData(res.data);
      setLastUpdate(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await settingsAPI.getStatsHistory(60);
      setStatsHistory(res.data?.history || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); fetchHistory(); }, [fetchData, fetchHistory]);

  // 2-second live refresh
  useEffect(() => {
    const liveInterval = setInterval(() => fetchData(), 2000);
    const historyInterval = setInterval(() => fetchHistory(), 60000);
    return () => { clearInterval(liveInterval); clearInterval(historyInterval); };
  }, [fetchData, fetchHistory]);

  const parseApiBalance = (raw) => {
    if (!raw) return 'N/A';
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed.MESSAGE || parsed.Balance || raw;
    } catch { return raw; }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
      </div>
    </DashboardLayout>
  );

  const s = data?.server || {};
  const cpu = data?.cpu || {};
  const mem = data?.memory || {};
  const d = data?.disk || {};
  const db = data?.database || {};
  const u = data?.users || {};
  const r = data?.recharges || {};
  const f = data?.finance || {};
  const c = data?.content || {};

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="server-info-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Home className="w-4 h-4" /><ChevronRight className="w-3 h-3" />
          <span className="text-slate-400">Admin</span><ChevronRight className="w-3 h-3" />
          <span className="font-medium text-slate-700">System Monitor</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">System Monitor</h1>
            <p className="text-xs text-slate-400 mt-1">
              Live (2s refresh) {lastUpdate && `\u00b7 Last: ${lastUpdate.toLocaleTimeString('en-IN')}`}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing} className="h-8 text-xs" data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />{refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* ====== SERVER INFO HERO ====== */}
        <div className="si-hero rounded-2xl overflow-hidden relative">
          <div className="absolute inset-0 si-mesh-bg" />
          <div className="relative z-10 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Server className="w-4 h-4 text-white/40" />
              <span className="text-[10px] font-bold text-white/30 uppercase tracking-[0.15em]">Server Info</span>
              <Badge className="ml-auto bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />ONLINE
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'PHP', value: s.php_version, icon: Cpu },
                { label: 'MySQL', value: s.mysql_version?.split('-')[0], icon: Database },
                { label: 'Server', value: s.server_software, icon: Globe },
                { label: 'Uptime', value: s.uptime || 'N/A', icon: Clock },
              ].map((item, i) => (
                <div key={i} className="si-server-stat">
                  <item.icon className="w-3.5 h-3.5 text-white/25 mb-1.5" />
                  <p className="text-sm font-bold text-white font-mono">{item.value}</p>
                  <p className="text-[9px] text-white/25 font-semibold uppercase tracking-wider">{item.label}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/5">
              {[
                { label: 'Memory Limit', value: s.memory_limit },
                { label: 'Max Upload', value: s.max_upload },
                { label: 'Max POST', value: s.max_post },
                { label: 'Max Exec Time', value: s.max_exec_time },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-[10px] text-white/20">{item.label}</span>
                  <span className="text-[11px] text-white/50 font-mono font-bold">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ====== REAL-TIME CPU / MEMORY / TREND ====== */}
        {cpu.load_1m !== undefined && mem.percent !== undefined && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* CPU Load Gauge */}
            <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="cpu-gauge">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-4 h-4 text-[#4680ff]" />
                  <span className="text-sm font-bold text-slate-800">CPU Load</span>
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full font-mono font-bold" style={{
                    background: cpu.load_1m > 20 ? '#fef2f2' : cpu.load_1m > 10 ? '#fffbeb' : '#f0fdf4',
                    color: cpu.load_1m > 20 ? '#dc2626' : cpu.load_1m > 10 ? '#d97706' : '#16a34a',
                  }}>{cpu.load_1m > 20 ? 'HIGH' : cpu.load_1m > 10 ? 'MODERATE' : 'NORMAL'}</span>
                </div>
                <div className="flex items-center justify-center gap-6">
                  <div className="relative">
                    <CircularGauge value={cpu.load_1m} label="1 MIN" size={85} color="#4338ca" maxVal={Math.max(32, cpu.load_1m * 1.5)} />
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 w-10">5min</span>
                      <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-violet-500 transition-all duration-700" style={{ width: `${Math.min((cpu.load_5m / Math.max(32, cpu.load_5m * 1.5)) * 100, 100)}%` }} />
                      </div>
                      <span className="text-[10px] text-slate-600 font-mono font-bold w-8">{cpu.load_5m}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 w-10">15min</span>
                      <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-slate-400 transition-all duration-700" style={{ width: `${Math.min((cpu.load_15m / Math.max(32, cpu.load_15m * 1.5)) * 100, 100)}%` }} />
                      </div>
                      <span className="text-[10px] text-slate-600 font-mono font-bold w-8">{cpu.load_15m}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Memory Gauge */}
            <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="memory-gauge">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-bold text-slate-800">Memory</span>
                  <Badge variant="outline" className="ml-auto text-[10px] font-mono">{mem.percent}%</Badge>
                </div>
                <div className="flex items-center justify-center">
                  <div className="relative">
                    <CircularGauge value={mem.percent} label="USED" size={85} color="#059669" />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                  <div className="p-2 rounded-lg bg-emerald-50/60">
                    <p className="text-xs font-bold text-emerald-800 font-mono">{(mem.used_mb / 1024).toFixed(1)} GB</p>
                    <p className="text-[8px] text-emerald-500 font-bold uppercase">Used</p>
                  </div>
                  <div className="p-2 rounded-lg bg-slate-50">
                    <p className="text-xs font-bold text-slate-700 font-mono">{(mem.total_mb / 1024).toFixed(1)} GB</p>
                    <p className="text-[8px] text-slate-400 font-bold uppercase">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trend Chart */}
            <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="trend-chart">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-violet-600" />
                  <span className="text-sm font-bold text-slate-800">Trend</span>
                  <span className="text-[9px] text-slate-400 ml-auto">Last {statsHistory.length} readings</span>
                </div>
                {statsHistory.length > 1 ? (
                  <div className="h-[140px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={statsHistory.slice(-30)} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4338ca" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#4338ca" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#059669" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="recorded_at" tickFormatter={(v) => { try { return new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } }} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '8px', border: '1px solid #e2e8f0' }} labelFormatter={(v) => { try { return new Date(v).toLocaleTimeString('en-IN'); } catch { return ''; } }} />
                        <Area type="monotone" dataKey="cpu_load_1m" name="CPU Load" stroke="#4338ca" strokeWidth={1.5} fill="url(#cpuGrad)" dot={false} />
                        <Area type="monotone" dataKey="memory_percent" name="Memory %" stroke="#059669" strokeWidth={1.5} fill="url(#memGrad)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[140px] flex flex-col items-center justify-center text-slate-300">
                    <BarChart3 className="w-8 h-8 mb-2 opacity-20" />
                    <p className="text-[10px] text-slate-400">Collecting data...</p>
                  </div>
                )}
                <div className="flex items-center justify-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#4680ff]" /><span className="text-[9px] text-slate-400">CPU Load</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-600" /><span className="text-[9px] text-slate-400">Memory %</span></div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ====== DISK & DATABASE ====== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="disk-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <HardDrive className="w-4 h-4 text-violet-600" />
                <span className="text-sm font-bold text-slate-800">Disk Usage</span>
                <Badge variant="outline" className="ml-auto text-[10px]">{d.percent}%</Badge>
              </div>
              <ProgressBar value={d.used_gb} max={d.total_gb} color="#7c3aed" />
              <div className="flex justify-between mt-2.5">
                <span className="text-[10px] text-slate-400">Used: <b className="text-slate-600 font-mono">{d.used_gb?.toLocaleString()} GB</b></span>
                <span className="text-[10px] text-slate-400">Total: <b className="text-slate-600 font-mono">{d.total_gb?.toLocaleString()} GB</b></span>
              </div>
              <p className="text-[10px] text-emerald-600 mt-1.5 font-medium">Free: {d.free_gb?.toLocaleString()} GB</p>
            </CardContent>
          </Card>

          <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="db-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-4 h-4 text-[#4680ff]" />
                <span className="text-sm font-bold text-slate-800">Database</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-blue-50/60 border border-blue-100">
                  <p className="text-lg font-bold text-blue-800 font-mono">{db.size_mb} MB</p>
                  <p className="text-[9px] text-blue-400 font-bold uppercase">DB Size</p>
                </div>
                <div className="p-3 rounded-xl bg-violet-50/60 border border-violet-100">
                  <p className="text-lg font-bold text-violet-800 font-mono">{db.table_count}</p>
                  <p className="text-[9px] text-violet-400 font-bold uppercase">Tables</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[10px] text-slate-400">Hostname:</span>
                <span className="text-[10px] text-slate-600 font-mono truncate">{s.hostname}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-slate-400">IP:</span>
                <span className="text-[10px] text-slate-600 font-mono truncate">{s.server_ip}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ====== LIVE STATS ====== */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={IndianRupee} label="Today's Volume" value={`\u20b9${Number(r.today_volume).toLocaleString('en-IN')}`} sub={`${r.today_count} recharges`} accent="#4338ca" testId="stat-volume" />
          <StatCard icon={Wallet} label="Total Wallet" value={`\u20b9${Number(f.total_wallet_balance).toLocaleString('en-IN')}`} sub="all users combined" accent="#059669" testId="stat-wallet" />
          <StatCard icon={IndianRupee} label="Today's Commission" value={`\u20b9${Number(f.today_commission).toFixed(2)}`} sub={`Total: \u20b9${Number(f.total_commission).toFixed(0)}`} accent="#7c3aed" testId="stat-commission" />
          <StatCard icon={Zap} label="API Balance" value={`\u20b9${parseApiBalance(data?.api_balance)}`} sub="Recharge API balance" accent="#ea580c" testId="stat-api-bal" />
        </div>

        {/* ====== RECHARGE STATUS + USERS ====== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="recharge-status-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-[#4680ff]" />
                <span className="text-sm font-bold text-slate-800">Today's Recharges</span>
                <Badge variant="outline" className="ml-auto text-[10px] font-mono">{r.today_count} total</Badge>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Success', count: r.today_success, icon: CheckCircle, color: '#059669', bg: 'bg-emerald-50' },
                  { label: 'Pending', count: r.today_pending, icon: AlertCircle, color: '#d97706', bg: 'bg-amber-50' },
                  { label: 'Failed', count: r.today_failed, icon: XCircle, color: '#dc2626', bg: 'bg-red-50' },
                ].map((st, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${st.bg} flex items-center justify-center`}>
                        <st.icon className="w-3.5 h-3.5" style={{ color: st.color }} />
                      </div>
                      <span className="text-xs font-medium text-slate-700">{st.label}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900 font-mono">{st.count}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[10px] text-slate-400">Last Recharge</span>
                <span className="text-[10px] text-slate-600 font-mono">{r.last_recharge}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-slate-400">All Time Total</span>
                <span className="text-[10px] text-slate-600 font-mono font-bold">{r.total}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="users-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-bold text-slate-800">Users & Content</span>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { label: 'Total Users', value: u.total, icon: Users, color: '#4338ca' },
                  { label: 'Active', value: u.active, icon: CheckCircle, color: '#059669' },
                  { label: 'Retailers', value: u.retailers, icon: ArrowUpRight, color: '#7c3aed' },
                  { label: 'Banners', value: c.active_banners, icon: Image, color: '#ea580c' },
                  { label: 'Operators', value: c.operator_codes, icon: Layers, color: '#0891b2' },
                  { label: 'Bill Ops', value: c.bill_operators, icon: Zap, color: '#d97706' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${item.color}10` }}>
                      <item.icon className="w-3.5 h-3.5" style={{ color: item.color }} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800 font-mono">{item.value}</p>
                      <p className="text-[9px] text-slate-400">{item.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
